import { useId } from 'react';

import { MARK_BITS, MARK_CELL, MARK_GAP, MARK_ROWS } from './brandMarkGrid';

/** The lattice, as (row, col) pairs in reading order. */
const CELLS: ReadonlyArray<readonly [number, number]> = MARK_ROWS.flatMap((line, row) =>
  [...line].flatMap((ch, col) => (ch === '#' ? [[row, col] as const] : [])),
);

/**
 * Lit cells merged into `[row, firstCol, length]` runs — 26 rectangles instead
 * of 108. The gaps come back as a mask, so the letterform stays one shape and
 * the server HTML stays a fraction of the size.
 */
const SPANS: ReadonlyArray<readonly [number, number, number]> = MARK_ROWS.flatMap((line, row) => {
  const runs: Array<readonly [number, number, number]> = [];
  let col = 0;
  while (col < line.length) {
    if (line[col] !== '#') {
      col += 1;
      continue;
    }
    const start = col;
    while (col < line.length && line[col] === '#') col += 1;
    runs.push([row, start, col - start] as const);
  }
  return runs;
});

const COLS = MARK_ROWS[0]!.length;
const GRID_W = COLS * MARK_CELL;
const GRID_H = MARK_ROWS.length * MARK_CELL;

/** Square board for the solid cut, half a cell of air on every side. */
const TILE = Math.max(GRID_W, GRID_H) + MARK_CELL;
const TILE_OX = (TILE - GRID_W) / 2;
const TILE_OY = (TILE - GRID_H) / 2;

/** Room to the left of the stem for the dissolve, and the padding around it. */
const TRAIL = 88;
const PAD = 4;
const WIDE_W = TRAIL + GRID_W;
const WIDE_H = GRID_H + PAD * 2;

/**
 * Opaque, inside a <mask>, means "keep this pixel"; black means "drop it".
 *
 * ADR-012's no-literal-colours rule is about branding landing in one file, and
 * this is not branding — it is an alpha stencil with no visible ink of its own.
 * The ink comes from the gradient underneath, which reads `--hs-color-mark-*`
 * exactly as ADR-012 asks. Hoisted out of the JSX because a disable comment
 * cannot sit between two attributes of one element.
 */
// eslint-disable-next-line no-restricted-syntax
const MASK_KEEP = '#fff';

/**
 * How far row `row`'s dissolve streak reaches back from the stem.
 *
 * Read off the bitstream rather than randomised, so the ragged edge is the
 * same on the server, on the client, and in `scripts/brand/logo.py`, which
 * computes it identically for the standalone files.
 */
function trailLength(row: number): number {
  const at = (row * 11) % MARK_BITS.length;
  return 26 + (parseInt(MARK_BITS.slice(at, at + 3) || '0', 2) % 5) * 15;
}

export type BrandMarkVariant = 'solid' | 'digits';

export interface BrandMarkProps {
  /**
   * `solid` is the bare lattice — the only cut that survives being 28px in a
   * header, where digits collapse into grey noise. `digits` is the display
   * mark: every cell carries one bit of the company's name in ASCII, and the
   * stem dissolves into the trail the approved artwork drew. Use it where
   * there is room to read it.
   */
  variant?: BrandMarkVariant | undefined;
  className?: string | undefined;
  /**
   * The accessible name. Omit it — the default — wherever the mark sits next
   * to the wordmark or inside a link that already names itself, which is every
   * current use: a logo announced twice is worse than one not announced at all.
   */
  title?: string | undefined;
}

/**
 * The Bitwize mark: the letter B built out of binary digits.
 *
 * Drawn inline rather than loaded as a file, for one reason that matters — the
 * gradient reads `--hs-color-mark-*`, so the mark re-inks itself when the theme
 * flips. A pre-inked `<img>` cannot: the ramp that reads on the admin's white
 * page vanishes on its dark one, which is exactly why the artwork this replaces
 * needed a hand-cut `_dark` variant of every file. Costing nothing extra is a
 * bonus — no request, no flash, sharp at every size.
 *
 * The lattice itself comes from `brandMarkGrid.ts`, which `make logo` writes
 * from `scripts/brand/logo.py`. Edit the letterform there, not here.
 */
export function BrandMark({ variant = 'solid', className, title }: BrandMarkProps) {
  // Two marks on one page (the header's and the footer's) would otherwise
  // define the same gradient id twice, and a browser resolves a duplicate id to
  // whichever came first — so the footer would silently wear the header's ink.
  const uid = useId().replace(/[^\w-]/g, '');
  const gradient = `bw-mark-${uid}`;
  const mask = `bw-cells-${uid}`;
  const trail = `bw-trail-${uid}`;

  const label = title ? { role: 'img', 'aria-label': title } : { 'aria-hidden': true };

  const stops = (
    <linearGradient
      id={gradient}
      gradientUnits="userSpaceOnUse"
      {...(variant === 'solid'
        ? { x1: TILE_OX, y1: TILE_OY + GRID_H, x2: TILE_OX + GRID_W, y2: TILE_OY }
        : { x1: TRAIL, y1: PAD + GRID_H, x2: WIDE_W, y2: PAD })}
    >
      {/* Bottom-left dark, top-right light — the approved artwork's own axis. */}
      <stop offset="0" stopColor="var(--hs-color-mark-from)" />
      <stop offset="0.55" stopColor="var(--hs-color-mark-mid)" />
      <stop offset="1" stopColor="var(--hs-color-mark-to)" />
    </linearGradient>
  );

  /*
    A horizontal alpha ramp over the trail zone: nothing at the far left, most
    of the way there by the time it reaches the stem. A mask rather than a
    per-streak `opacity`, so the dissolve is continuous instead of stepping
    once per row.
  */
  const trailMask = (
    <>
      <linearGradient
        id={`${trail}-fade`}
        gradientUnits="userSpaceOnUse"
        x1={0}
        y1={0}
        x2={TRAIL}
        y2={0}
      >
        <stop offset="0" stopColor={MASK_KEEP} stopOpacity="0" />
        <stop offset="1" stopColor={MASK_KEEP} stopOpacity="0.75" />
      </linearGradient>
      <mask id={trail}>
        <rect width={TRAIL} height={WIDE_H} fill={`url(#${trail}-fade)`} />
      </mask>
    </>
  );

  if (variant === 'solid') {
    return (
      <svg viewBox={`0 0 ${TILE} ${TILE}`} className={className} focusable="false" {...label}>
        <defs>
          {stops}
          {/*
            The lattice as a mask rather than as 108 separate squares: one
            repeating gap grid, anchored to the grid origin so the gaps land
            between cells instead of across them.
          */}
          <pattern
            id={`${mask}-cells`}
            x={TILE_OX}
            y={TILE_OY}
            width={MARK_CELL}
            height={MARK_CELL}
            patternUnits="userSpaceOnUse"
          >
            <rect
              x={MARK_GAP / 2}
              y={MARK_GAP / 2}
              width={MARK_CELL - MARK_GAP}
              height={MARK_CELL - MARK_GAP}
              rx={2.5}
              fill={MASK_KEEP}
            />
          </pattern>
          <mask id={mask}>
            <rect width="100%" height="100%" fill={`url(#${mask}-cells)`} />
          </mask>
        </defs>
        <g fill={`url(#${gradient})`} mask={`url(#${mask})`}>
          {SPANS.map(([row, col, length]) => (
            <rect
              key={`${row}-${col}`}
              x={TILE_OX + col * MARK_CELL}
              y={TILE_OY + row * MARK_CELL}
              width={length * MARK_CELL}
              height={MARK_CELL}
            />
          ))}
        </g>
      </svg>
    );
  }

  return (
    <svg viewBox={`0 0 ${WIDE_W} ${WIDE_H}`} className={className} focusable="false" {...label}>
      <defs>
        {stops}
        {trailMask}
      </defs>

      {/*
        The dissolve. One streak per row, running *into* the stem rather than
        stopping short of it — detached dashes read as a texture behind the
        letter, where the artwork's trail reads as the letter itself smearing.
        Length varies per row off the bitstream, so the edge is ragged the way
        motion is, and is still identical on every build.
      */}
      <g fill={`url(#${gradient})`} mask={`url(#${trail})`}>
        {MARK_ROWS.map((_, row) => {
          const length = trailLength(row);
          return (
            <rect
              key={row}
              x={TRAIL - length}
              y={PAD + row * MARK_CELL + MARK_CELL / 2 - 3.5}
              width={length}
              height={7}
              rx={3.5}
            />
          );
        })}
      </g>

      {/*
        `textLength` per digit, not just a font size: a monospace zero advances
        about 0.6em, so at any size that fits the cell the glyphs sit in it with
        a third of the cell empty on each side — and the letterform goes so airy
        that the bowls' counters stop reading as holes. Stretching each digit to
        the cell closes that up, and is why this cut resolves at 240px where the
        unstretched one needed twice that.
      */}
      <g
        fill={`url(#${gradient})`}
        fontFamily="var(--hs-font-mono)"
        fontSize={MARK_CELL - 1}
        fontWeight={700}
        textAnchor="middle"
        dominantBaseline="central"
      >
        {CELLS.map(([row, col], i) => (
          <text
            key={`${row}-${col}`}
            x={TRAIL + col * MARK_CELL + MARK_CELL / 2}
            y={PAD + row * MARK_CELL + MARK_CELL / 2}
            textLength={MARK_CELL - 3}
            lengthAdjust="spacingAndGlyphs"
          >
            {MARK_BITS[i % MARK_BITS.length]}
          </text>
        ))}
      </g>
    </svg>
  );
}
